import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { ROUTES } from '../../App';
import { 
  Home, 
  FileText, 
  History, 
  BarChart2, 
  Receipt, 
  Calculator, 
  User, 
  Box, 
  LifeBuoy, 
  CreditCard,
  Building2,
  Briefcase,
  Plus
} from 'lucide-react';
import coolyLogo from '../../assets/coolyLogo.png';

// Helper function from your old code to determine if a link is active
const isActivePath = (path, currentPathname) => {
  if (currentPathname === path) return true;
  if (path !== ROUTES.DASHBOARD && path !== ROUTES.HOME && currentPathname.startsWith(path + '/')) return true;
  return false;
};

const Sidebar = ({ user, currentUser, isAdmin, subscriptionStatus, userRole, open, setOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isPro = useMemo(() =>
    subscriptionStatus?.subscription?.level === 'pro' &&
    (subscriptionStatus?.subscription?.status === 'active' || subscriptionStatus?.subscription?.status === 'trialing'),
    [subscriptionStatus]
  );

  const isSubscriptionEffectivelyFree = useMemo(() => {
    const sub = subscriptionStatus?.subscription;
    if (!sub) return true;
    // CRITICAL FIX: Include past_due in effectively free logic to block access to premium features
    return sub.level === 'free' || 
           sub.status === 'ended' || 
           sub.status === 'canceled' || 
           sub.status === 'incomplete_expired' ||
           sub.status === 'past_due'; // ADDED: Treat past_due as effectively free
  }, [subscriptionStatus]);

  // Using the structure from your current sidebar for defining items
  const practitionerMainMenuItems = [
    { name: 'Dashboard', href: ROUTES.DASHBOARD, icon: Home, pro: false, alwaysAccessible: true },
    { name: 'Create Invoice', href: ROUTES.INVOICE, icon: FileText, pro: false },
    { name: 'Invoice History', href: ROUTES.INVOICE_HISTORY, icon: History, pro: true },
    { name: 'Reports', href: ROUTES.REPORTS, icon: BarChart2, pro: true },
    { name: 'Expenses', href: ROUTES.EXPENSES, icon: Receipt, pro: true },
    { name: 'Tax Calculator', href: ROUTES.TAX, icon: Calculator, pro: true },
    { name: 'Jobs & Vacancies', href: ROUTES.JOBS, icon: Briefcase, pro: false, beta: true }, // Moved below Tax Calculator
  ];

  const practitionerAccountItems = [
    { name: 'Profile', href: ROUTES.PROFILE, icon: User, alwaysAccessible: true },
    { name: 'Resources', href: ROUTES.RESOURCES, icon: Box, alwaysAccessible: true },
    { name: 'Support', href: ROUTES.SUPPORT, icon: LifeBuoy, alwaysAccessible: true },
    { name: 'Billing', href: ROUTES.BILLING, icon: CreditCard, alwaysAccessible: true },
  ];

  const practiceManagerNavItems = [
    { name: 'Practice Dashboard', href: ROUTES.PRACTICE_DASHBOARD, icon: Building2, alwaysAccessible: true },
    { name: 'Post New Job', href: ROUTES.PRACTICE_JOB_NEW, icon: Plus, alwaysAccessible: true },
    { name: 'Manage Practices', href: ROUTES.PRACTICE_VERIFY, icon: Building2, alwaysAccessible: true },
  ];
  
  const practiceManagerAccountItems = [
    { name: 'Profile', href: ROUTES.PROFILE, icon: User, alwaysAccessible: true },
    { name: 'Resources', href: ROUTES.RESOURCES, icon: Box, alwaysAccessible: true },
    { name: 'Support', href: ROUTES.SUPPORT, icon: LifeBuoy, alwaysAccessible: true },
  ];


  let currentMainItems;
  let currentAccountItems;
  let mainSectionTitle = "Main Menu"; // Default title

  if (userRole === 'Practice Manager') {
    currentMainItems = practiceManagerNavItems;
    currentAccountItems = practiceManagerAccountItems;
    mainSectionTitle = "Practice Management";
  } else {
    currentMainItems = practitionerMainMenuItems;
    currentAccountItems = practitionerAccountItems;
    // mainSectionTitle remains "Main Menu"
  }

  const renderNavItem = (item) => {
    const active = isActivePath(item.href, location.pathname);
    
    const proFeatureNotActive = item.pro && !isPro;
    const freeAccessRestricted = isSubscriptionEffectivelyFree && !item.alwaysAccessible && !item.pro;
    const isDisabled = proFeatureNotActive || freeAccessRestricted;

    let titleTooltip = item.name;
    if (proFeatureNotActive) titleTooltip = `${item.name} (Pro Feature - Upgrade Required)`;
    else if (freeAccessRestricted) titleTooltip = `${item.name} (Subscription Required)`;
    
    // Theme-aware styles
    const baseLinkClasses = "flex items-center space-x-3 px-3 py-2 rounded-md transition-colors duration-150 text-sm";
    const activeClasses = "bg-purple-600 text-white"; 
    const inactiveClasses = "text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-white";
    const disabledClasses = "text-gray-400 dark:text-slate-500 opacity-60 cursor-not-allowed";

    return (
      <Link
        key={item.name}
        to={isDisabled ? '#' : item.href}
        className={cn(
          baseLinkClasses,
          isDisabled ? disabledClasses : (active ? activeClasses : inactiveClasses)
        )}
        aria-disabled={isDisabled}
        onClick={(e) => {
          if (isDisabled) {
            e.preventDefault();
          }
          if (open && setOpen) {
            setOpen(false);
          }
        }}
        title={titleTooltip}
      >
        <item.icon className="h-4 w-4" />
        <span>{item.name}</span>
        {proFeatureNotActive && <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold text-black bg-green-400 rounded-md border border-green-500 leading-none">PRO</span>}
        {item.beta && <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold text-white bg-blue-500 rounded-md border border-blue-600 leading-none">BETA</span>}
      </Link>
    );
  };

  return (
    <aside className={cn(
      "bg-background border-r border-border flex flex-col", 
      "transition-transform duration-300 ease-in-out",
      "fixed inset-y-0 left-0 z-50 w-64", 
      open ? "translate-x-0" : "-translate-x-full lg:translate-x-0" 
    )}>
      {/* Logo container - FIXED: Exact height match with header */}
      <div className="flex h-[72px] items-center border-b border-border px-4 lg:h-[65px] lg:px-6">
        <Link to={ROUTES.HOME} className="flex items-center gap-2 font-semibold">
          <img src={coolyLogo} alt="Coooly Logo" className="h-8 w-auto" />
        </Link>
      </div>
      
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {currentMainItems.length > 0 && (
          <div className="mb-5">
            {/* MODIFIED: Use theme-aware text color for section header */}
            <h3 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {mainSectionTitle}
            </h3>
            <div className="space-y-1">
              {currentMainItems.map(renderNavItem)}
            </div>
          </div>
        )}

        {currentAccountItems.length > 0 && (
           <div className="mb-5">
            {/* MODIFIED: Use theme-aware text color for section header */}
            <h3 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Account
            </h3>
            <div className="space-y-1">
              {currentAccountItems.map(renderNavItem)}
            </div>
          </div>
        )}
      </nav>
      
      {/* Optional Footer - can be removed if not needed */}
      {/* <div className="mt-auto p-4 border-t border-border">
         <p className="text-xs text-muted-foreground text-center">&copy; {new Date().getFullYear()} Coooly</p>
      </div> */}
    </aside>
  );
};

export default Sidebar;
